// Shared options, field definitions and styling for the Duty Estimator admin screens.
//
// These mirror the Final Duty Estimator Formula. Every rate and fee below is stored in the
// database and edited here — Section 6 requires that nothing be hardcoded. The helper text is
// written for a non-developer, because these numbers decide what every buyer is quoted.

export const CONDITION_OPTIONS = [
  { value: "any", label: "Any condition" },
  { value: "new", label: "New" },
  { value: "used", label: "Used" },
];

export const FUEL_TYPE_OPTIONS = [
  { value: "any", label: "Any fuel" },
  { value: "petrol", label: "Petrol" },
  { value: "diesel", label: "Diesel" },
  { value: "hybrid", label: "Hybrid" },
  { value: "electric", label: "Electric" },
];

export const VEHICLE_TYPE_OPTIONS = [
  { value: "passenger_car", label: "Passenger Cars" },
  { value: "bus", label: "Buses & Coaches" },
  { value: "truck", label: "Trucks, Lorries & Tippers" },
];

// Ghana Customs' own freight quotation categories (client-supplied), used for the Freight
// Rates admin table. Since 2026-07-31 the user enters CIF directly, so this is no longer
// consumed by the calculation itself — it's kept for the standalone Freight Rates reference tab.
export const BODY_TYPE_OPTIONS = [
  { value: "saloon_sedan_hatchback", label: "Saloon / Sedan / Hatchback" },
  { value: "suv", label: "SUV" },
  { value: "pickup_van_bus", label: "Pickup / Van / Bus" },
  { value: "articulator_truck_head", label: "Articulator Truck Head" },
];

// 2026-08-04: a duty rate row can be scoped to a body style within a category (e.g. Pickup vs
// SUV within "Cars"), matching the client's "Vehicle Type Duty Rate Reference Guide" vocabulary —
// deliberately broader than BODY_TYPE_OPTIONS above (Freight Rates' 4-bucket list). This is only
// a UI suggestion list; the backend field is a free string, not a fixed enum.
export const DUTY_RATE_BODY_TYPE_OPTIONS = [
  { value: "saloon_sedan_hatchback", label: "Saloon / Sedan / Hatchback" },
  { value: "coupe", label: "Coupe" },
  { value: "convertible", label: "Convertible" },
  { value: "estate_wagon", label: "Estate / Station Wagon" },
  { value: "suv", label: "SUV" },
  { value: "crossover", label: "Crossover" },
  { value: "mpv_minivan", label: "MPV / Minivan" },
  { value: "pickup_double_cab", label: "Pickup (Double Cab)" },
  { value: "pickup_single_cab", label: "Pickup (Single Cab)" },
  { value: "cargo_van", label: "Cargo Van" },
  { value: "passenger_van", label: "Passenger Van" },
  { value: "minibus", label: "Minibus" },
  { value: "bus", label: "Bus" },
  { value: "light_truck", label: "Light Truck" },
  { value: "heavy_truck", label: "Heavy Truck" },
  { value: "truck_tractor", label: "Truck Tractor" },
  { value: "trailer", label: "Trailer" },
  { value: "semi_trailer", label: "Semi-Trailer" },
  { value: "motorcycle", label: "Motorcycle" },
  { value: "atv", label: "ATV / Quad Bike" },
  { value: "earth_moving_equipment", label: "Earth Moving Equipment" },
  { value: "agricultural_tractor", label: "Agricultural Tractor" },
  { value: "boat", label: "Boat / Marine Craft" },
];

// 2026-08-04: the statutory levies a category can be exempted from or charged a different rate
// for — kept in sync with CategoryLevyOverride.LEVY_KEYS on the backend.
export const LEVY_KEY_OPTIONS = [
  { value: "ecowasLevyRate", label: "ECOWAS Levy" },
  { value: "auLevyRate", label: "AU Levy" },
  { value: "eximLevyRate", label: "EXIM Levy" },
  { value: "specialImportLevyRate", label: "Special Import Levy" },
  { value: "nhilRate", label: "NHIL" },
  { value: "getFundRate", label: "GETFund Levy" },
  { value: "importVatRate", label: "Import VAT" },
  { value: "withholdingTaxRate", label: "Withholding Tax" },
];

export const AUDIT_LOG_ENTITY_OPTIONS = [
  { value: "", label: "All" },
  { value: "setting", label: "Settings" },
  { value: "duty_rate", label: "Duty Rate" },
  { value: "depreciation_band", label: "Depreciation Band" },
  { value: "over_age_penalty_band", label: "Over-age Penalty Band" },
  { value: "freight_rate", label: "Freight Rate" },
  { value: "category_levy_override", label: "Category Levy Override" },
];

// Settings grouped in the order the formula runs, so an admin reading top to bottom follows the
// same sequence the calculation does. `type` decides which control is rendered.
export const SETTINGS_GROUPS = [
  {
    title: "Exchange Rate",
    caption: "Used to convert the CIF value into cedis. Customs Value = CIF × this rate.",
    fields: [
      { key: "usdToGhsRate", label: "USD to GHS Rate", type: "number" },
      { key: "fxRateSource", label: "Rate Source", type: "text", helper: "Where it came from, e.g. Bank of Ghana." },
      { key: "fxRateUpdatedAt", label: "Rate Effective From", type: "datetime", helper: "When this rate was set." },
    ],
  },
  {
    title: "Levies on Customs Value",
    caption: "Each charged on the Customs Value. Confirmed by the client's own worked examples (2026-07-30).",
    fields: [
      { key: "ecowasLevyRate", label: "ECOWAS Levy (%)", type: "number", helper: "Confirmed at 0.5%." },
      { key: "auLevyRate", label: "AU Levy (%)", type: "number", helper: "Confirmed at 0.2%." },
      { key: "eximLevyRate", label: "EXIM Levy (%)", type: "number", helper: "Confirmed at 0.75%." },
      {
        key: "specialImportLevyRate",
        label: "Special Import Levy (%)",
        type: "number",
        helper: "The source document left this undefined, but the client's own examples both confirm 2%.",
      },
    ],
  },
  {
    title: "NHIL, GETFund & Import VAT",
    caption:
      "Official GRA/ICUMS methodology (confirmed by the client 2026-08-04 over their own worked examples): NHIL and GETFund are percentages of Customs Value + Import Duty — the same base Import VAT uses. The COVID-19 Health Recovery Levy is excluded entirely.",
    fields: [
      { key: "nhilRate", label: "NHIL (%)", type: "number", helper: "2.5% of Customs Value + Import Duty." },
      { key: "getFundRate", label: "GETFund Levy (%)", type: "number", helper: "2.5% of Customs Value + Import Duty." },
      {
        key: "importVatRate",
        label: "Import VAT (%)",
        type: "number",
        helper: "15%, on Customs Value + Import Duty. Matches the Network Charge VAT rate below.",
      },
    ],
  },
  {
    title: "Withholding Tax",
    caption:
      "Neither of the client's two worked examples charges this, so it defaults to 0% and is left off the estimate entirely until a rate is set.",
    fields: [{ key: "withholdingTaxRate", label: "Withholding Tax (%)", type: "number" }],
  },
  {
    title: "Examination Fee",
    caption:
      "Confirmed 2026-08-21 with the client's Customs Officer contact: this is charged on every vehicle — new, used and motorcycles alike, not only used ones. A flat cedi amount or a percentage of Customs Value.",
    fields: [
      { key: "vehicleExaminationFeeType", label: "Examination Fee Type", type: "feeType" },
      { key: "vehicleExaminationFeeValue", label: "Examination Fee Value", type: "number", helper: "Currently 1% of Customs Value, pending reconfirmation with Customs." },
      {
        key: "inspectionFeeType",
        label: "Inspection Fee Type (legacy, unused)",
        type: "feeType",
        helper: "No longer read by the calculation — the Examination Fee above now applies to every vehicle. Kept in case a separately-confirmed inspection charge is added later.",
      },
      { key: "inspectionFeeValue", label: "Inspection Fee Value (legacy, unused)", type: "number" },
    ],
  },
  {
    title: "Network Charge",
    caption:
      "Official methodology: 0.4% of Customs Value (nominally FOB, but there's no separate FOB figure now the user enters CIF directly), cascading NHIL, GETFund and VAT — all three, each at the rate set above, not just a VAT.",
    fields: [
      { key: "networkChargeType", label: "Charged On", type: "networkChargeType" },
      { key: "networkChargeValue", label: "Network Charge Value", type: "number", helper: "0.4%." },
      { key: "networkChargeVatRate", label: "Network Charge VAT (%)", type: "number", helper: "15%." },
    ],
  },
  {
    title: "Flat Statutory Fees",
    caption: "Fixed cedi amounts added to every estimate.",
    fields: [
      { key: "shippersAuthorityFee", label: "Ghana Shippers Authority Fee (GHS)", type: "number", helper: "Confirmed at GHS 12." },
      { key: "idfFee", label: "Import Declaration Form (IDF) Fee (GHS)", type: "number", helper: "Confirmed at GHS 5." },
      {
        key: "ghsDisinfectionFee",
        label: "Ghana Health Service Disinfection Fee (GHS)",
        type: "number",
        helper: "Confirmed at a flat GHS 150 in the client's own examples.",
      },
    ],
  },
  {
    title: "Processing Fee",
    caption:
      "Added 2026-08-04 at the client's request, defaulted to 0 (disabled). Per the client, this is charged only on general goods, not vehicles — so it's expected to stay off here, but is configurable rather than hardcoded away in case that changes.",
    fields: [
      { key: "processingFeeType", label: "Processing Fee Type", type: "feeType" },
      { key: "processingFeeValue", label: "Processing Fee Value", type: "number", helper: "0 = disabled." },
    ],
  },
  {
    title: "Customer Disclaimer",
    caption: "Shown underneath every estimate in the app.",
    fields: [{ key: "disclaimer", label: "Disclaimer", type: "textarea", full: true }],
  },
];

export const SETTINGS_FIELDS = SETTINGS_GROUPS.flatMap((group) => group.fields);
export const NUMERIC_SETTINGS_KEYS = SETTINGS_FIELDS.filter((f) => f.type === "number").map((f) => f.key);

// The dashboard theme flattens outlined inputs (assets/theme/components/form): the label
// line-height is squashed to 0.9, selects get `padding: 0 12px !important`, and their dropdown
// icon is hidden outright. Left alone the label collides with the outline and a select's value
// sits cramped against it. This restores standard outlined-field metrics for the estimator forms.
export const INPUT_SX = {
  // Every field is pinned to one height rather than left to size itself: an empty select has
  // nothing but a zero-width space inside, so it otherwise collapses to its padding and the
  // resting label lands underneath the outline.
  "& .MuiInputBase-root:not(.MuiInputBase-multiline)": {
    minHeight: "44px",
  },
  "& .MuiInputLabel-root": {
    lineHeight: 1.4375,
    fontSize: "0.875rem",
    // Placed by hand because the theme's squashed line-height leaves MUI's default transforms
    // (written for 56px fields) sitting too low. 0.85 matches the 0.85em notch the theme cuts,
    // so the label fills its gap in the outline exactly.
    "&:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 12px) scale(1)" },
    "&.MuiInputLabel-shrink": {
      lineHeight: 1.4375,
      fontSize: "0.875rem",
      transform: "translate(14px, -8px) scale(0.85)",
    },
  },
  // Multiline fields carry their padding on the root, but the theme zeroes it out there too.
  "& .MuiInputBase-multiline": { padding: "12px" },
  "& .MuiOutlinedInput-input:not(.MuiInputBase-inputMultiline)": {
    paddingTop: "12px",
    paddingBottom: "12px",
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    boxSizing: "content-box",
    minHeight: "20px !important",
    padding: "12px 32px 12px 12px !important",
  },
  "& .MuiSelect-icon": { display: "inline-flex !important" },
};

export const WHITE_BUTTON_SX = {
  color: "#2e7d32 !important",
  backgroundColor: "#fff !important",
  "&:hover": { backgroundColor: "#f1f1f1 !important" },
};

export const GREEN_BUTTON_SX = {
  color: "#fff !important",
  backgroundColor: "#2e7d32 !important",
  "&:hover": { backgroundColor: "#1b5e20 !important" },
};

export const formatLabel = (value) =>
  String(value ?? "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

export const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-GH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Ages are stored in months because the depreciation table needs 6- and 18-month boundaries,
// but the source tables are written in years, so both are shown.
export const formatAgeRange = (minAgeMonths, maxAgeMonths) => {
  const years = (months) => (Number(months) / 12).toFixed(1);
  if (maxAgeMonths === null || maxAgeMonths === undefined || maxAgeMonths === "") {
    return `${minAgeMonths}+ months (over ${years(minAgeMonths)} yrs)`;
  }
  return `${minAgeMonths}–${maxAgeMonths} months (${years(minAgeMonths)}–${years(maxAgeMonths)} yrs)`;
};

export const formatCcRange = (min, max) => {
  const hasMin = min !== null && min !== undefined && min !== "";
  const hasMax = max !== null && max !== undefined && max !== "";
  if (!hasMin && !hasMax) return "Any";
  if (hasMin && hasMax) return `${min}–${max}cc`;
  return hasMin ? `${min}cc+` : `Up to ${max}cc`;
};
